import { DataProxy } from 'apollo-cache'
import {
  CredentialsFragment,
  GetUserCredentialsQuery,
  GetUserCredentialsDocument,
} from 'gqlTypes'
import {
  Authorization,
  AuthorizationId,
  areAuthorizationIdsEqual,
  isPersonalAuthorization,
  isSsoAuthorization,
  PersonalAuthorization,
  SsoAuthorization,
} from './authorizations'
import isEqual from 'lodash.isequal'
import { log } from 'utils/log'
import { getParsedItem, setStringifiedItem } from 'utils/localStorage'
import localStorageKeys from 'constants/localStorageKeys'

interface WorkspaceForAuth {
  ssoEnabled: boolean
  identifier: string
}

const replaceAuth = (
  allAuthorizations: Authorization[],
  authorization: Authorization
) =>
  allAuthorizations.map(auth =>
    areAuthorizationIdsEqual(auth, authorization) ? authorization : auth
  )

export const getUserAuthorization = (cache: DataProxy) => {
  try {
    const data = cache.readQuery<GetUserCredentialsQuery>({
      query: GetUserCredentialsDocument,
    })

    return data?.userCredentials || null
  } catch (e) {
    return null
  }
}

export const setUserAuthorization = (
  cache: DataProxy,
  authorization: Authorization
) => {
  addAuthorization(authorization)
  setApolloCredentials(authorization.fragment, cache)
}

export const getAllAuthorizations = (): Authorization[] =>
  getParsedItem(localStorageKeys.userAllAuthorizations) ?? []

export const getActiveAuthorization = (
  cache: DataProxy
): Authorization | undefined => {
  const credentials = readCredentials(cache)

  const allAuthorizations = getAllAuthorizations()

  return allAuthorizations.find(auth =>
    isEqual(auth.fragment, credentials?.userCredentials)
  )
}

/**
 * Inserts a new authorization in the list of credentials we have. If the new
 * authorization already matches an existing one (type and workspaceId are
 * equal) then we replace it.
 * @param authorization New authorization to add to the list
 */
export const addAuthorization = (authorization: Authorization) => {
  const allAuthorizations = getAllAuthorizations()

  const isAuthAlreadyStored = allAuthorizations?.find(auth =>
    areAuthorizationIdsEqual(auth, authorization)
  )

  const newAuthorizations = isAuthAlreadyStored
    ? replaceAuth(allAuthorizations, authorization)
    : [...allAuthorizations, authorization]

  setStringifiedItem(localStorageKeys.userAllAuthorizations, newAuthorizations)
}

export const removeActiveAuthorization = (cache: DataProxy) => {
  const activeAuth = getActiveAuthorization(cache)
  if (!activeAuth) return

  removeAuthorizationByAuthToken(activeAuth.fragment.authToken)
  setFallbackAuthorizationId(cache)
}

export const removeAuthorizationByAuthToken = (authToken: string) => {
  const allAuthorizations = getAllAuthorizations()

  const index = allAuthorizations.findIndex(
    auth => auth.fragment.authToken === authToken
  )
  const removedAuth = allAuthorizations.splice(index, 1)[0]

  setStringifiedItem(localStorageKeys.userAllAuthorizations, allAuthorizations)

  return removedAuth
}

/**
 * Replace all authorizations by the one provided. This is useful to entirely
 * switch accounts.
 */
export const replaceAllAuthorizations = (authorization: Authorization) => {
  setStringifiedItem(localStorageKeys.userAllAuthorizations, [authorization])
}

/**
 * Replaces an already existent session with the newly negotiated tokens.
 *
 * @param cache Apollo cache's instance
 * @param fragment New credentials fragment
 * @param previousAuthToken The previous auth token used to uniquely identify
 *  the item we want to replace
 */
export const replaceAuthorization = (
  cache: DataProxy,
  fragment: CredentialsFragment,
  previousAuthToken: string
) => {
  const removedAuth = removeAuthorizationByAuthToken(previousAuthToken)
  const newAuth = { ...removedAuth, fragment: fragment }
  setUserAuthorization(cache, newAuth)
}

/**
 * Sets the active authorization for a workspace. If the workspace has SSO
 * enabled then we try setting the current session as the one with authorization
 * for that workspace. We use a personal session otherwise.
 */
export const setActiveAuthorizationIdForWorkspace = (
  workspace: WorkspaceForAuth,
  cache: DataProxy
) => {
  const { ssoEnabled, identifier } = workspace

  if (ssoEnabled) {
    setActiveAuthorizationId({ type: 'sso', workspaceId: identifier }, cache)
  } else {
    setActiveAuthorizationId({ type: 'personal' }, cache)
  }
}

/**
 * Configures active credentials picking one from the list of sessions.
 * This is used as a default value in case the current route doesn't set any
 * active credentials.
 */
export const setFallbackAuthorizationId = (cache: DataProxy) => {
  const allAuthorizations = getAllAuthorizations()
  const personalAuthorization = allAuthorizations.find(isPersonalAuthorization)

  const auth = personalAuthorization ?? allAuthorizations[0]

  setApolloCredentials(auth?.fragment, cache)
}

export const setActiveAuthorizationId = (
  authId: AuthorizationId,
  cache: DataProxy
) => {
  const allAuthorizations = getAllAuthorizations()

  const activeAuthorization = allAuthorizations.find(auth =>
    areAuthorizationIdsEqual(auth, authId)
  )

  const personalAuthorization = allAuthorizations.find(isPersonalAuthorization)

  /**
   * We fallback first to personal authorization as it's the default token we
   * used for everything before introducing SSO. Then, if there is no personal
   * token we just get the first stored authorization.
   */
  const auth =
    activeAuthorization ?? personalAuthorization ?? allAuthorizations[0]

  log(`Session -> Setting active auth to ${authId.type}. Using ${auth?.type}`)

  setApolloCredentials(auth?.fragment, cache)
}

export const setApolloCredentials = (
  credentials: CredentialsFragment | null,
  cache: DataProxy
) => {
  const previousCredentials = readCredentials(cache)

  /**
   * If both credentials are exactly the same we avoid updating them. This
   * prevents Apollo from firing update events that all subscriptions would
   * receive.
   */
  if (isEqual(previousCredentials?.userCredentials, credentials)) {
    return
  }

  cache.writeQuery<GetUserCredentialsQuery>({
    query: GetUserCredentialsDocument,
    data: {
      __typename: 'RootQueryType',
      userCredentials: credentials,
    },
  })
}

const readCredentials = (cache: DataProxy) => {
  try {
    return cache.readQuery<GetUserCredentialsQuery>({
      query: GetUserCredentialsDocument,
    })
  } catch (error) {
    return undefined
  }
}

/**
 * Find the authorization of type personal (user/password)
 * among all the authorizations of the user.
 */
export function getPersonalAuthorization(): PersonalAuthorization | null {
  const allAuths = getAllAuthorizations()
  const personalAuth = allAuths.find(isPersonalAuthorization)

  return personalAuth || null
}

/**
 * Find the authorization of type SSO corresponding to the provided
 * workspace Id among all the authorizations of the user.
 */
export function getSsoAuthorizationForWorkspace(
  workspaceId: string
): SsoAuthorization | null {
  const allAuths = getAllAuthorizations()

  const ssoAuths = allAuths.filter(isSsoAuthorization)
  const workspaceSsoAuth = ssoAuths.find(
    auth => auth.workspaceId === workspaceId
  )
  return workspaceSsoAuth || null
}
